import type { Operation } from '@apollo/client'
import type { NetworkError } from '@apollo/client/errors'
import type { AxiosError } from 'axios'
import type { GraphQLError, GraphQLFormattedError } from 'graphql'

import type { LutherException } from '@acre/graphql'

/**
 * Set `context: { suppressErrorToast: true }` on a query or mutation whose caller presents its own
 * failure message in context, beside the fields the user has to correct. The generic toast names the
 * endpoint rather than the problem, so it would otherwise appear alongside the specific message.
 * Reporting to Sentry is unaffected.
 */
export const suppressesErrorToast = (operation: Operation) => Boolean(operation.getContext().suppressErrorToast)

export const isForbiddenError = <
  T extends 'graphql' | 'network',
  E extends T extends 'graphql' ? GraphQLFormattedError : NetworkError,
>(
  err: E,
  type: T,
) => {
  return getStatusCode(err, type) === 401
}

export const getStatusCode = <
  T extends 'graphql' | 'network',
  E extends T extends 'graphql' ? GraphQLFormattedError : NetworkError,
>(
  err: E,
  type: T,
): number | false => {
  if (type === 'graphql') {
    const { extensions, originalError } = err as GraphQLError

    if (extensions?.status) {
      const status = Number(extensions?.status)
      if (!isNaN(status) && status !== 0) return status
    }

    if (originalError) {
      if (
        Object.prototype.hasOwnProperty.call(originalError, 'response') &&
        (originalError as AxiosError).response?.status
      ) {
        const status = Number((originalError as AxiosError).response?.status)
        if (!isNaN(status) && status !== 0) return status
      }

      if ((originalError as LutherException)?.statusCode) {
        const status = Number((originalError as LutherException).statusCode)
        if (!isNaN(status) && status !== 0) return status
      }
    }
  }
  return false
}
